import { ESocketEvents } from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { io, Socket } from 'socket.io-client'
import customParser from 'socket.io-msgpack-parser'
import { useStore } from 'stores'

interface RpSocketContextType {
  socket: Socket | null
  isConnected: boolean
  emit: (event: string, ...args: any[]) => void
  emitWithAck: (event: string, ...args: any[]) => Promise<any>
  on: (event: string, listener: (...args: any[]) => void) => void
  off: (event: string, listener?: (...args: any[]) => void) => void
}

const RPSocketContext = createContext<RpSocketContextType | undefined>(
  undefined
)

export const useRpSocketContext = () => {
  const context = useContext(RPSocketContext)
  if (!context) {
    throw new Error('useRpSocketContext must be used within RPSocketProvider')
  }
  return context
}

interface IRpSocketProviderProps {
  children: ReactNode
}

const url = `${process.env.REACT_APP_PRP_2BOX_API}/prp2b_player`

export const RpSocketProvider = observer(
  ({ children }: IRpSocketProviderProps) => {
    const { loginStore } = useStore()
    const { accessToken } = loginStore
    const playerId = loginStore.user?.id || ''
    const socketRef = useRef<Socket | null>(null)
    const [isConnected, setIsConnected] = useState(false)

    const emit = useCallback((event: string, ...args: any[]) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit(event, ...args)
      }
    }, [])

    const emitWithAck = useCallback(
      (event: string, ...args: any[]): Promise<any> => {
        return new Promise((resolve, reject) => {
          if (socketRef.current?.connected) {
            socketRef.current.emit(event, ...args, (response: any) => {
              if (response?.success) {
                resolve(response)
              } else {
                reject(new Error(response?.error || ''))
              }
            })
          } else {
            reject(new Error('Socket is not connected'))
          }
        })
      },
      []
    )

    const on = useCallback(
      (event: string, listener: (...args: any[]) => void) => {
        if (socketRef.current) {
          socketRef.current.on(event, listener)
        }
      },
      []
    )

    const off = useCallback(
      (event: string, listener?: (...args: any[]) => void) => {
        if (socketRef.current) {
          if (listener) {
            socketRef.current.off(event, listener)
          } else {
            socketRef.current.off(event)
          }
        }
      },
      []
    )

    useEffect(() => {
      if (!playerId || !accessToken) return undefined

      const socket = io(url, {
        query: { playerId, accessToken },
        forceNew: true,
        secure: true,
        transports: ['websocket'],
        parser: customParser,
      })

      socketRef.current = socket

      socket.on(ESocketEvents.CONNECT, () => {
        setIsConnected(true)
      })

      socket.on(ESocketEvents.DISCONNECT, () => {
        setIsConnected(false)
      })

      socket.on(ESocketEvents.CONNECT_ERROR, () => {
        setIsConnected(false)
      })

      return () => {
        socket.removeAllListeners()
        socket.disconnect()

        socketRef.current = null

        setIsConnected(false)
      }
    }, [playerId])

    const contextValue = useMemo(
      () => ({
        socket: socketRef.current,
        isConnected,
        emit,
        emitWithAck,
        on,
        off,
      }),
      [isConnected, emit, emitWithAck, on, off]
    )

    return (
      <RPSocketContext.Provider value={contextValue}>
        {children}
      </RPSocketContext.Provider>
    )
  }
)
